import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  useProjects,
  useCreateProject,
  useUpdateProject,
  useDeleteProject,
  useUpdateProjectStatus,
} from '../../../../hooks/useProjects';
import { useSubscribers } from '../../../../hooks/useSubscribers';
import { useAllotments } from '../../../../hooks/useAllotments';
import { useLots } from '../../../../hooks/useLots';
import { useSubcontractors } from '../../../../hooks/useSubcontractors';
import { useTaskTemplatesByType } from '../../../../hooks/useTaskTemplates';
import { useAssignTemplatesToProject } from '../../../../hooks/useProjects';
import useProjectFiltersStore from '../../../../stores/useProjectFiltersStore';

/**
 * Hook personalizado para la gestión completa de proyectos
 * Migrado desde la lógica de DocAssemble con funcionalidad moderna
 * Maneja todo el estado y lógica del dashboard de proyectos
 */
export const useProjectManagement = () => {
  // Estados locales
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(500);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [progressDialogActiveTab, setProgressDialogActiveTab] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);

  // Estado del snackbar para notificaciones
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  // Datos del formulario
  const [formData, setFormData] = useState({
    uuid_lot: null,
    name: '',
    description: '',
    status: 'not_started',
    start_date: '',
    end_date: '',
    progress_template: '', // Template de progreso obligatorio
    quality_template: '', // Template de calidad obligatorio
  });

  // Hooks de TanStack Query para datos
  const { data: projects = [], isLoading: projectsLoading } = useProjects();
  const { data: subscribers = [], isLoading: subscribersLoading } = useSubscribers();
  const { data: allotments = [] } = useAllotments();
  const { data: lots = [], isLoading: lotsLoading } = useLots();
  const { data: subcontractors = [], isLoading: subcontractorsLoading } = useSubcontractors();
  const { data: progressTemplates = [] } = useTaskTemplatesByType('progress');
  const { data: qualityTemplates = [] } = useTaskTemplatesByType('quality');

  const {
    filters,
    appliedFilters,
    setFilter,
    applyFilters,
    clearFilters,
  } = useProjectFiltersStore();

  const createProjectMutation = useCreateProject();
  const updateProjectMutation = useUpdateProject();
  const deleteProjectMutation = useDeleteProject();
  const updateStatusMutation = useUpdateProjectStatus();
  const assignTemplatesMutation = useAssignTemplatesToProject();

  const lotsById = useMemo(() => {
    return lots.reduce((acc, lot) => {
      if (lot?.uuid) {
        acc[lot.uuid] = lot;
      }
      return acc;
    }, {});
  }, [lots]);

  const getAssignedTemplateId = useCallback((project, templates) => {
    const assignedTemplateIds = (project?.project_task_assignments || [])
      .filter((assignment) => assignment.is_active !== false)
      .map((assignment) => assignment.uuid_task_template);
    return templates.find((template) => assignedTemplateIds.includes(template.uuid))?.uuid || '';
  }, []);

  const resolveProjectSubscribers = useCallback(
    (project) => {
      if (project?.contract?.subscribers?.length) {
        return project.contract.subscribers;
      }
      if (Array.isArray(project?.subscribers) && project.subscribers.length > 0) {
        if (typeof project.subscribers[0] === 'object') {
          return project.subscribers;
        }
        return subscribers.filter((s) => project.subscribers.includes(s.uuid));
      }
      return [];
    },
    [subscribers]
  );

  // Filtrar proyectos por término de búsqueda y filtros aplicados
  const filteredProjects = projects.filter((project) => {
    const searchTermLower = searchTerm.trim().toLowerCase();
    const contractFilter = appliedFilters.contract_number?.trim().toLowerCase();
    const subscriberFilter = appliedFilters.subscriber_name?.trim().toLowerCase();

    if (appliedFilters.uuid_allotment) {
      const projectLot = lotsById[project.uuid_lot];
      const projectAllotmentId = projectLot?.uuid_allotment || '';
      if (projectAllotmentId !== appliedFilters.uuid_allotment) {
        return false;
      }
    }

    if (appliedFilters.uuid_subcontractor) {
      if (project.uuid_subcontractor !== appliedFilters.uuid_subcontractor) {
        return false;
      }
    }

    if (appliedFilters.status) {
      if (project.status !== appliedFilters.status) {
        return false;
      }
    }

    if (contractFilter) {
      const contractNumber = project.contract?.contract_number?.toLowerCase() || '';
      if (!contractNumber.includes(contractFilter)) {
        return false;
      }
    }

    if (subscriberFilter) {
      const projectSubscribers = resolveProjectSubscribers(project);
      const hasSubscriberMatch = projectSubscribers.some((subscriber) => {
        const subscriberFirstName = subscriber.first_name?.toLowerCase() || '';
        const subscriberLastName = subscriber.last_name?.toLowerCase() || '';
        const fullName = `${subscriberFirstName} ${subscriberLastName}`.trim().toLowerCase();
        return (
          subscriberFirstName.includes(subscriberFilter) ||
          subscriberLastName.includes(subscriberFilter) ||
          fullName.includes(subscriberFilter)
        );
      });
      if (!hasSubscriberMatch) {
        return false;
      }
    }

    if (!searchTermLower) return true;

    const nameMatch = project.name?.toLowerCase().includes(searchTermLower);
    const descriptionMatch = project.description?.toLowerCase().includes(searchTermLower);
    const contractNumberMatch = project.contract?.contract_number
      ?.toLowerCase()
      .includes(searchTermLower);

    const projectSubscribers = resolveProjectSubscribers(project);
    const subscribersMatch = projectSubscribers.some((subscriber) => {
      const subscriberFirstName = subscriber.first_name?.toLowerCase() || '';
      const subscriberLastName = subscriber.last_name?.toLowerCase() || '';
      const fullName = `${subscriberFirstName} ${subscriberLastName}`.trim().toLowerCase();
      return (
        subscriberFirstName.includes(searchTermLower) ||
        subscriberLastName.includes(searchTermLower) ||
        fullName.includes(searchTermLower)
      );
    });

    return nameMatch || descriptionMatch || contractNumberMatch || subscribersMatch;
  });

  const nameCollator = new Intl.Collator('es-AR', {
    numeric: true,
    sensitivity: 'base',
  });

  const sortedProjects = [...filteredProjects].sort((a, b) => {
    const nameA = a?.name || '';
    const nameB = b?.name || '';
    return nameCollator.compare(nameA, nameB);
  });

  // Paginación
  const totalPages = Math.max(1, Math.ceil(sortedProjects.length / rowsPerPage));
  const totalCount = sortedProjects.length;
  const paginatedProjects = sortedProjects.slice(
    (page - 1) * rowsPerPage,
    (page - 1) * rowsPerPage + rowsPerPage
  );

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  // Calcular estadísticas del dashboard
  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilter(name, value);
  };

  const handleApplyFilters = () => {
    applyFilters();
    setPage(1);
  };

  const handleClearFilters = () => {
    clearFilters();
    setPage(1);
  };

  // Funciones de manejo de búsqueda
  const handleSearch = useCallback((value) => {
    setSearchTerm(value);
    setPage(1);
  }, []);

  // Funciones de paginación
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event) => {
    const value = parseInt(event.target.value, 500);
    if (!Number.isNaN(value)) {
      setRowsPerPage(value);
      setPage(1);
    }
  };

  // Funciones de diálogos principales
  const handleOpenDialog = () => {
    setEditMode(false);
    setSelectedProject(null);
    setFormData({
      project_number: '',
      uuid_lot: null,
      name: '',
      description: '',
      status: 'not_started',
      construction_type: 'not_assigned',
      start_date: '',
      end_date: '',
      subscribers: [],
      progress_template: '',
      quality_template: '',
    });
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedProject(null);
    setFormData({
      project_number: '',
      uuid_lot: null,
      name: '',
      description: '',
      status: 'not_started',
      construction_type: 'not_assigned',
      start_date: '',
      end_date: '',
      subscribers: [],
      progress_template: '',
      quality_template: '',
    });
  };

  // Funciones de diálogos específicos
  const handleOpenProgressDialog = (project) => {
    setSelectedProject(project);
    setProgressDialogOpen(true);
    setProgressDialogActiveTab(0);
  };

  const handleCloseProgressDialog = () => {
    setProgressDialogOpen(false);
    setSelectedProject(null);
    setProgressDialogActiveTab(0);
  };

  // Funciones de formulario
  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSelectChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async (projectData) => {
    const dataToSave = projectData || formData;

    // Solo validar templates en modo creación, no en edición
    if (!editMode && (!dataToSave.progress_template || !dataToSave.quality_template)) {
      throw new Error('Debes seleccionar templates de progreso y calidad');
    }

    try {
      if (editMode && selectedProject) {
        // En modo edición, solo enviar los campos editables (no templates)
        const { progress_template, quality_template, project_task_assignments, ...editableData } =
          dataToSave;

        const currentProgressTemplate = getAssignedTemplateId(selectedProject, progressTemplates);
        const currentQualityTemplate = getAssignedTemplateId(selectedProject, qualityTemplates);
        const nextProgressTemplate =
          progress_template !== undefined ? progress_template : currentProgressTemplate;
        const nextQualityTemplate =
          quality_template !== undefined ? quality_template : currentQualityTemplate;
        const templatesChanged =
          nextProgressTemplate !== currentProgressTemplate ||
          nextQualityTemplate !== currentQualityTemplate;

        if (templatesChanged) {
          if (!nextProgressTemplate || !nextQualityTemplate) {
            throw new Error('Debes seleccionar plantillas de progreso y calidad');
          }
        }

        await updateProjectMutation.mutateAsync({
          id: selectedProject.uuid,
          data: editableData,
        });

        if (templatesChanged) {
          await assignTemplatesMutation.mutateAsync({
            id: selectedProject.uuid,
            templateUuids: [nextProgressTemplate, nextQualityTemplate],
          });
        }
      } else {
        // En modo creación, incluir templates
        const {
          progress_template,
          quality_template,
          project_subscribers: subscribersData,
          subscribers,
          ...projectData
        } = dataToSave;

        const subscribersList = subscribersData || subscribers || [];

        const project_subscribers = subscribersList.map((subscriber) => ({
          uuid_subscriber: subscriber.uuid || subscriber.uuid_subscriber || subscriber,
          role: subscriber.role || 'subscriber',
          assigned_date: new Date().toISOString().split('T')[0],
        }));

        const template_uuids = [progress_template, quality_template].filter(Boolean);

        const cleanedProjectData = {
          ...projectData,
        };

        if (!cleanedProjectData.end_date || cleanedProjectData.end_date.trim() === '') {
          delete cleanedProjectData.end_date;
        }

        const unifiedProjectData = {
          ...cleanedProjectData,
          project_subscribers,
          template_uuids,
        };

        await createProjectMutation.mutateAsync(unifiedProjectData);
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error al guardar proyecto:', error);
      throw error;
    }
  };

  const handleEdit = (project) => {
    setEditMode(true);
    setSelectedProject(project);

    const projectProgressTemplate = getAssignedTemplateId(project, progressTemplates);
    const projectQualityTemplate = getAssignedTemplateId(project, qualityTemplates);

    setFormData({
      project_number: project.project_number || '',
      name: project.name || '',
      description: project.description || '',
      status: project.status || 'not_started',
      construction_type: project.construction_type || 'not_assigned',
      start_date: project.start_date || '',
      end_date: project.end_date || '',
      subscribers: project.subscribers || [],
      progress_template: projectProgressTemplate,
      quality_template: projectQualityTemplate,
    });
    setDialogOpen(true);
  };

  const handleDelete = (project) => {
    setProjectToDelete(project);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (projectToDelete) {
      try {
        await deleteProjectMutation.mutateAsync(projectToDelete.uuid);
        setDeleteConfirmOpen(false);
        setProjectToDelete(null);
      } catch (error) {
        console.error('Error al eliminar proyecto:', error);
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setProjectToDelete(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleStatusChange = async (project, newStatus) => {
    try {
      await updateStatusMutation.mutateAsync({
        id: project.uuid,
        status: newStatus,
      });
    } catch (error) {
      console.error('Error al cambiar estado:', error);
    }
  };

  const handleConstructionTypeChange = async (project, newConstructionType) => {
    try {
      await updateProjectMutation.mutateAsync({
        id: project.uuid,
        data: { construction_type: newConstructionType },
      });
    } catch (error) {
      console.error('Error al cambiar tipo de construcción:', error);
    }
  };

  const handleViewProgress = (project) => {
    setSelectedProject(project);
    setProgressDialogOpen(true);
    setProgressDialogActiveTab(0); // Tab de progreso
  };

  const handleViewQuality = (project) => {
    setSelectedProject(project);
    setProgressDialogOpen(true);
    setProgressDialogActiveTab(1); // Tab de calidad
  };

  // Loading state
  const isLoading =
    projectsLoading || subscribersLoading || lotsLoading || subcontractorsLoading;

  return {
    // Data principal
    projects: paginatedProjects,
    allProjects: projects,
    lots,
    isLoading,
    page,
    totalPages,
    totalCount,
    rowsPerPage,
    searchTerm,

    // Estados de diálogos
    dialogOpen,
    progressDialogOpen,
    progressDialogActiveTab,
    editMode,
    selectedProject,

    // Datos de soporte
    subscribers,
    allotments,
    subcontractors,
    progressTemplates,
    qualityTemplates,

    // Filtros
    filters,
    handleFilterChange,
    handleApplyFilters,
    handleClearFilters,

    // Funciones de manejo
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleOpenDialog,
    handleCloseDialog,
    handleOpenProgressDialog,
    handleCloseProgressDialog,
    handleInputChange,
    handleSelectChange,
    handleSave,
    handleEdit,
    handleDelete,
    handleStatusChange,
    handleConstructionTypeChange,
    handleViewProgress,
    handleViewQuality,

    // Diálogos de confirmación
    deleteConfirmOpen,
    projectToDelete,
    handleConfirmDelete,
    handleCancelDelete,

    // Notificaciones
    snackbar,
    handleCloseSnackbar,

    // Form data
    formData,
  };
};
